<template>
  <!--
    The Phone view's "Move to…" bottom sheet (design frame "Phone — Check-in", the ⋯ sheet). Mounted only while open (the view v-ifs it).
    It lists Check-in, every room, Check-out, Dismiss and Send alert for ONE patient. A room that holds someone else is greyed and
    cannot be picked — it says who is in it ("In use · Okafor, Daniel") — so the phone never offers a move the board would refuse
    (plan behaviour call 1). The sheet decides nothing and writes nothing: it reports the choice, the view hands it to the page.
  -->
  <div class="ops-sheet" data-test="ops-move-sheet">
    <div class="ops-sheet__backdrop" data-test="ops-move-sheet-backdrop" @click="$emit('close')"></div>
    <div class="ops-sheet__card" role="dialog" aria-modal="true" :aria-label="`Move ${name}`">
      <div class="ops-sheet__grab" aria-hidden="true"></div>
      <div class="ops-sheet__head">
        <span class="ops-sheet__name" data-test="ops-move-sheet-name">{{ name }}</span>
        <button type="button" class="ops-sheet__close" aria-label="Close" data-test="ops-move-sheet-close" @click="$emit('close')">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>
      <div class="ops-sheet__title">Move to…</div>
      <div class="ops-sheet__rows">
        <button
          v-for="row in moveRows"
          :key="`move-${row.id}`"
          type="button"
          class="ops-sheet__row"
          :class="{ 'ops-sheet__row--off': row.disabled }"
          :disabled="row.disabled"
          :data-test="`ops-move-sheet-${row.id}`"
          @click="$emit('move', { card, to: row.id })"
        >
          <span class="ops-sheet__label">{{ row.label }}</span>
          <span v-if="row.sub" class="ops-sheet__sub">{{ row.sub }}</span>
        </button>

        <button
          v-if="card.column !== 'checkout'"
          type="button"
          class="ops-sheet__row ops-sheet__row--accent"
          data-test="ops-move-sheet-checkout"
          @click="$emit('checkout', card)"
        ><span class="ops-sheet__label">Check out</span><span class="ops-sheet__sub">Opens notes</span></button>

        <button
          v-if="canDismiss && !card.dismissed"
          type="button"
          class="ops-sheet__row ops-sheet__row--danger"
          data-test="ops-move-sheet-dismiss"
          @click="$emit('dismiss', card)"
        ><span class="ops-sheet__label">Dismiss</span></button>

        <button
          v-if="!card.dismissed"
          type="button"
          class="ops-sheet__row"
          data-test="ops-move-sheet-ping"
          @click="$emit('ping', card)"
        ><span class="ops-sheet__label">Send alert</span></button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Events: move({ card, to })   `to` is 'checkin' or an OperatoryNum (a string) — the same ids the Board's ⋯ menu uses
 *         checkout(card) · dismiss(card) · ping(card) · close
 *
 * ⛔ No prop is named `patNum`, `siteID`, `loading` or `allSettings` (mixin.js computeds shadow them).
 */
import { cardLocation, cardLastFirst } from '@/utils/practiceOps/moves';

export default {
  name: 'OpsMoveSheet',

  props: {
    /** The board.js card whose ⋯ was tapped. */
    card: { type: Object, required: true },
    /** board.js rooms: { opNum, label, cards, occupied } */
    rooms: { type: Array, default: () => [] },
    canDismiss: { type: Boolean, default: false },
  },

  computed: {
    name() {
      return cardLastFirst(this.card);
    },

    /** Check-in, then every room, minus the card's own place. Occupied rooms are greyed and cannot be picked. */
    moveRows() {
      const here = cardLocation(this.card);
      const rows = [];
      if (here !== 'checkin') rows.push({ id: 'checkin', label: 'Check-in', sub: '', disabled: false });
      this.rooms.forEach((room) => {
        if (String(room.opNum) === here) return;
        const other = (room.cards || []).find(c => String(c.patNum) !== String(this.card.patNum));
        rows.push({
          id: String(room.opNum),
          label: room.label,
          sub: other ? `In use · ${cardLastFirst(other)}` : 'Open',
          disabled: !!other,
        });
      });
      return rows;
    },
  },

  mounted() {
    window.addEventListener('keydown', this.onKeydown);
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown);
  },

  methods: {
    onKeydown(e) {
      if (e && e.key === 'Escape') this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
// Above the AI agent's floating button (z 9999) and the shell's tab bar, as the checklist sheet is.
.ops-sheet {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10000;
}

.ops-sheet__backdrop {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(12, 15, 14, 0.32);
}

.ops-sheet__card {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  max-height: 88%;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 8px 16px calc(16px + env(safe-area-inset-bottom));
  border-radius: 18px 18px 0 0;
  background: #ffffff;
  box-shadow: 0 -8px 28px rgba(12, 15, 14, 0.18);
  color: #0c0f0e;
  font-family: inherit;
}

.ops-sheet__grab {
  width: 40px;
  height: 4px;
  margin: 0 auto 10px;
  border-radius: 2px;
  background: #c8cfcd;
}

.ops-sheet__head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ops-sheet__name {
  flex: 1;
  min-width: 0;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.01em;
}

.ops-sheet__close {
  flex: none;
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #3a4240;
  cursor: pointer;

  &:hover { background: #eff3f2; }
}

.ops-sheet__title {
  margin: 4px 0 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #707876;
}

.ops-sheet__rows {
  display: flex;
  flex-direction: column;
}

.ops-sheet__row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  padding: 0 4px;
  border: 0;
  border-bottom: 1px solid #eef2f1;
  background: transparent;
  font-family: inherit;
  text-align: left;
  color: #0c0f0e;
  cursor: pointer;

  &:last-child { border-bottom: 0; }
  &:hover:not(:disabled) { background: #f7faf9; }
}

.ops-sheet__label {
  flex: 1;
  font-size: 16px;
  font-weight: 700;
}

.ops-sheet__sub {
  font-size: 13px;
  color: #707876;
}

.ops-sheet__row--off {
  cursor: default;

  .ops-sheet__label { color: #9ca4a2; }
  .ops-sheet__sub { color: #9ca4a2; }
}

.ops-sheet__row--accent .ops-sheet__label { color: #0f7259; }
.ops-sheet__row--danger .ops-sheet__label { color: #c2333a; }
</style>
